import { css } from '@emotion/react'
import { Alert, Cell, Grid, Heading, Modal, ModalBody, Text, Theme, useTheme, VFlow } from 'bold-ui'
import { ModalHeader } from 'bold-ui/lib/components/Modal/ModalHeader'
import { useSession } from 'components/auth/useSession'
import { DateTime } from 'components/date/DateTime'
import { useFlags } from 'config/useFlagsContext'
import { useMedicamentoUsoModalQuery } from 'graphql/hooks.generated'
import { ReceitaMedicamento } from 'graphql/types.generated'
import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import { useHistory, useRouteMatch } from 'react-router'
import { getEditAtendProfId } from 'view/atendimentos/atendimento-individual/util-atendimentoIndividual'
import { grupoCboPrescricao } from 'view/atendimentos/detail/soap/plano/acessos'
import { AlertaInsulinaNph } from 'view/atendimentos/detail/soap/plano/prescricao-medicamento/components/AlertaInsulinaNph'
import {
  groupAndSortReceitaMedicamentoByReceitaUsoContinuoGrupoId,
  isInsulinaNph,
} from 'view/atendimentos/detail/soap/plano/prescricao-medicamento/utils/utils-prescricao'
import { formatCidOrCiap } from 'view/atendimentos/utils/format-atendimento'

import MedicamentosAccordion from './MedicamentosAccordion'
import { MedicamentosAccordionItemHeader } from './MedicamentosAccordionItemHeader'
import MedicamentosAccordionViewOnly from './MedicamentosAccordionViewOnly'
import MedicamentosConcluidosTable from './MedicamentosConcluidosTable'
import { MedicamentosTratamentoInterrompidoSection } from './MedicamentosTratamentoInterrompidoSection'
import { PrescricaoRenovadaLabel } from './PrescricaoRenovadaLabel'

interface MedicamentosAtivosConcluidosModalProps {
  prontuarioId: ID
  atendimentoProfissionalId: ID
  dataAtendimento: Instant
  isAtendimentoProcedimentos: boolean
  isAtendimentoObservacao: boolean
  editable: boolean
}

interface AlertInlineProps {
  visible: boolean
  message: string
}

function MedicamentosAtivosConcluidosModal(props: MedicamentosAtivosConcluidosModalProps) {
  const {
    prontuarioId,
    atendimentoProfissionalId,
    dataAtendimento,
    isAtendimentoProcedimentos,
    isAtendimentoObservacao = false,
    editable,
  } = props

  const [alertState, setAlertState] = useState<AlertInlineProps>({ visible: false, message: '' })
  const history = useHistory()
  const match = useRouteMatch()
  const { hasCboAuth } = useSession({ fetchPolicy: 'cache-only' })
  const { MOTIVO_PRESCRICAO_ENABLED } = useFlags()
  const theme = useTheme()
  const styles = createStyles(theme)

  const podeInterromperOuRenovarMedicamento = !isAtendimentoProcedimentos && hasCboAuth(grupoCboPrescricao.adicionar)

  const editAtendProfId = getEditAtendProfId(isAtendimentoObservacao, atendimentoProfissionalId)
  const {
    data: { listaMedicamento: listaMedicamentosAtivos },
    loading: loadingAtivos,
  } = useMedicamentoUsoModalQuery({
    variables: {
      input: {
        prontuarioId,
        editAtendProfId,
        usoContinuo: false,
        ativo: true,
        pageParams: { unpaged: true },
        ...(editable && { dataAtendimento }),
      },
    },
  })

  const renderDetalhesAdicionais = useCallback(
    (receita: ReceitaMedicamento) => {
      return (
        <Fragment>
          <VFlow vSpacing={0}>
            <Text fontWeight='bold'>Recomendações</Text>
            {receita.recomendacoes ? (
              <Text style={styles.recommendationsText}>{receita.recomendacoes}</Text>
            ) : (
              <Text fontStyle='italic'>Não há recomendações para o medicamento</Text>
            )}
          </VFlow>
          {MOTIVO_PRESCRICAO_ENABLED && (
            <VFlow vSpacing={0}>
              <Text fontWeight='bold'>Motivo da prescrição</Text>
              <Text>{formatCidOrCiap(receita.cid10, receita.ciap)}</Text>
            </VFlow>
          )}
        </Fragment>
      )
    },
    [MOTIVO_PRESCRICAO_ENABLED, styles.recommendationsText]
  )

  const listaGruposAtivosOrdenados = useMemo(() => {
    const medicamentos = (listaMedicamentosAtivos?.content as ReceitaMedicamento[]) || []

    return groupAndSortReceitaMedicamentoByReceitaUsoContinuoGrupoId(medicamentos)
  }, [listaMedicamentosAtivos])

  const hasInsulinaNph = useMemo(
    () =>
      !!listaMedicamentosAtivos?.content?.some((receita: ReceitaMedicamento) =>
        isInsulinaNph(receita.medicamento?.medicamentoCatmat?.codigoCatMat)
      ),
    [listaMedicamentosAtivos]
  )

  const onClose = useCallback(() => {
    return history.push(match.url.replace('/medicamentos-ativos-concluidos', ''))
  }, [history, match.url])

  useEffect(() => {
    if (alertState.visible) {
      setTimeout(() => {
        setAlertState({ visible: false, message: '' })
      }, 5000)
    }
  }, [alertState.visible])

  const renderItemBody = useCallback(
    (receita: ReceitaMedicamento) => {
      return (
        <VFlow style={styles.itemBodyContainer}>
          {receita.tratamentoInterrompido && <MedicamentosTratamentoInterrompidoSection receita={receita} />}
          {renderDetalhesAdicionais(receita)}
        </VFlow>
      )
    },
    [styles.itemBodyContainer, renderDetalhesAdicionais]
  )

  const renderGroupedBody = useCallback(
    (grupo: ReceitaMedicamento[]) => {
      const firstReceita = grupo.first()
      return (
        <div css={styles.groupedBody}>
          {firstReceita.tratamentoInterrompido && (
            <VFlow style={[styles.itemBodyContainer, styles.borderBottom]}>
              <MedicamentosTratamentoInterrompidoSection receita={firstReceita} />
            </VFlow>
          )}
          {grupo.map((receita, index) => {
            const isPrescricaoOriginal = index === grupo.length - 1

            return (
              <VFlow style={styles.groupedItem} key={receita.id}>
                {receita.atendProf &&
                  (isPrescricaoOriginal ? (
                    <Text fontWeight='bold'>
                      Prescrito em <DateTime value={receita.atendProf.iniciadoEm} />
                      {' por '}
                      {receita.atendProf.lotacao.profissional.nome}
                    </Text>
                  ) : (
                    <PrescricaoRenovadaLabel atendProf={receita.atendProf} />
                  ))}
                <VFlow vSpacing={0}>
                  <Heading level={5}>
                    {`${receita.medicamento.principioAtivo} ${receita.medicamento.concentracao ?? ''}`.trim()}
                  </Heading>
                  <Text>{receita.posologia}</Text>
                </VFlow>
                {renderDetalhesAdicionais(receita)}
              </VFlow>
            )
          })}
        </div>
      )
    },
    [styles.borderBottom, styles.groupedBody, styles.groupedItem, styles.itemBodyContainer, renderDetalhesAdicionais]
  )

  return (
    <Modal open={true} onClose={onClose}>
      <ModalHeader title='Medicamentos em uso/tratamento concluído' />
      <ModalBody>
        <Grid alignItems='flex-start' gap={0}>
          <Cell size={12}>
            {podeInterromperOuRenovarMedicamento && editable && (
              <Alert type='info'>
                Ao interromper tratamento, a medicação será apresentada como tratamento concluído e, ao renovar
                prescrição, a nova prescrição será agrupada com a anterior. As alterações serão refletidas somente no
                próximo atendimento.
              </Alert>
            )}
            {alertState.visible && (
              <div css={styles.alertSpacing}>
                <Alert type='success' inline onCloseClick={() => setAlertState({ visible: false, message: '' })}>
                  {alertState.message}
                </Alert>
              </div>
            )}
          </Cell>
          <Cell size={12}>
            <Heading level={2}>Medicamentos em uso</Heading>
            {hasInsulinaNph && (
              <div css={styles.insulinaNphAlert}>
                <AlertaInsulinaNph inline={false} />
              </div>
            )}
            {editable ? (
              <MedicamentosAccordion
                loading={loadingAtivos}
                grupos={listaGruposAtivosOrdenados}
                podeInterromperOuRenovarMedicamento={podeInterromperOuRenovarMedicamento}
                dataAtendimento={dataAtendimento}
                setAlertState={setAlertState}
                renderItemBody={renderItemBody}
                renderItemHeader={(receita, dataInicioGrupo, hasInterrompidoAgora) => (
                  <MedicamentosAccordionItemHeader
                    receita={receita}
                    dataInicioGrupo={dataInicioGrupo}
                    hasInterrompidoAgora={hasInterrompidoAgora}
                  />
                )}
                renderGroupedBody={renderGroupedBody}
              />
            ) : (
              <MedicamentosAccordionViewOnly
                loading={loadingAtivos}
                grupos={listaGruposAtivosOrdenados}
                renderItemBody={renderItemBody}
                renderItemHeader={(receita, dataInicioGrupo) => (
                  <MedicamentosAccordionItemHeader receita={receita} dataInicioGrupo={dataInicioGrupo} />
                )}
                renderGroupedBody={renderGroupedBody}
              />
            )}
          </Cell>
          <Cell size={12}>
            <Heading
              level={2}
              style={css`
                margin-bottom: 0.5rem;
              `}
            >
              Medicamentos com tratamento concluído
            </Heading>
            <MedicamentosConcluidosTable
              prontuarioId={prontuarioId}
              atendimentoProfissionalId={atendimentoProfissionalId}
              isAtendimentoObservacao={isAtendimentoObservacao}
              dataAtendimento={dataAtendimento}
              renderItemGroupHeader={(grupo) => (
                <MedicamentosAccordionItemHeader
                  receita={grupo.first()}
                  dataInicioGrupo={grupo.last().dtInicioTratamento}
                />
              )}
              renderItemGroupBody={(grupo) =>
                grupo.length > 1 ? renderGroupedBody(grupo) : renderItemBody(grupo.first())
              }
            />
          </Cell>
        </Grid>
      </ModalBody>
    </Modal>
  )
}

export default React.memo(MedicamentosAtivosConcluidosModal)

const createStyles = (theme: Theme) => ({
  alertSpacing: css`
    margin-top: 0.5rem;
  `,
  insulinaNphAlert: css`
    margin: 0.5rem 0;
  `,
  borderBottom: css`
    border-bottom: 1px solid ${theme.pallete.divider};
  `,
  interrompidoAgoraStyles: css`
    color: ${theme.pallete.primary.main};
    display: inline-flex;
    margin-left: 0.5rem;
    align-items: center;
    font-weight: normal;
  `,
  headerFlow: css`
    flex: 1;
  `,
  dateFlow: css`
    min-width: 8.6rem;
  `,
  itemBodyContainer: css`
    padding: 1rem;
  `,
  recommendationsText: css`
    white-space: pre-wrap;
    word-break: break-all;
  `,
  groupedBody: css`
    overflow-x: auto;
    max-height: 24rem;
  `,
  groupedItem: css`
    padding: 1rem;
    &:not(:last-child) {
      border-bottom: 1px solid ${theme.pallete.divider};
    }
  `,
})
